<template>
	<transition name="gd-fade">
		<div v-if="visible" class="gd" @click.self="$emit('close')">
			<div class="gd__panel">
				<header class="gd__head">
					<span class="gd__icon">
						<img v-if="isImageIcon" :src="category.icon" alt="">
						<template v-else>{{ category.icon || '📁' }}</template>
					</span>
					<h3 class="gd__title">{{ category.name }}</h3>
					<span class="gd__count">{{ bookmarks.length }} 个书签</span>
					<div class="gd__search">
						<input v-model="localKeyword" type="text" maxlength="50" placeholder="在分组内搜索">
					</div>
					<button type="button" class="gd__close" title="关闭" @click="$emit('close')">✕</button>
				</header>

				<div class="gd__body">
					<div class="gd__grid">
						<BookmarkCard
							v-for="bookmark in filteredBookmarks"
							:key="bookmark.id"
							:bookmark="bookmark"
							:edit-mode="editMode"
							@open="$emit('open', $event)"
							@edit="$emit('edit-bookmark', $event)"
							@remove="$emit('remove-bookmark', $event)"
						/>
						<button v-if="editMode" type="button" class="gd__add" @click="$emit('add-bookmark', category)">
							<span class="gd__add-plus">＋</span>
							<span>添加书签</span>
						</button>
					</div>
					<p v-if="!filteredBookmarks.length" class="gd__empty">{{ localKeyword ? '没有匹配的书签' : '该分组暂无书签' }}</p>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
	import BookmarkCard from './BookmarkCard'

	export default {
		name: 'GroupDialog',
		components: {BookmarkCard},
		props: {
			/**
			 * 是否显示弹窗
			 */
			visible: {
				type: Boolean,
				default: false
			},
			/**
			 * 分组数据
			 */
			category: {
				type: Object,
				default: null
			},
			/**
			 * 分组内书签列表（可能已被页面关键词过滤）
			 */
			bookmarks: {
				type: Array,
				default: () => []
			},
			/**
			 * 是否处于编辑模式
			 */
			editMode: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				localKeyword: ''
			}
		},
		computed: {
			/**
			 * 分组图标是否为图片地址
			 */
			isImageIcon() {
				return /^(http|data):/i.test((this.category && this.category.icon || '').trim())
			},
			/**
			 * 按弹窗内关键词过滤后的书签列表
			 */
			filteredBookmarks() {
				const keyword = this.localKeyword.trim().toLowerCase()
				if (!keyword) {
					return this.bookmarks
				}
				return this.bookmarks.filter(bookmark => {
					const title = (bookmark.title || '').toLowerCase()
					const url = (bookmark.url || '').toLowerCase()
					const description = (bookmark.description || '').toLowerCase()
					return title.includes(keyword) || url.includes(keyword) || description.includes(keyword)
				})
			}
		},
		watch: {
			/**
			 * 弹窗打开时清空搜索词并监听 ESC 关闭
			 */
			visible(value) {
				if (value) {
					this.localKeyword = ''
					document.addEventListener('keydown', this.handleKeydown)
				} else {
					document.removeEventListener('keydown', this.handleKeydown)
				}
			}
		},
		beforeDestroy() {
			document.removeEventListener('keydown', this.handleKeydown)
		},
		methods: {
			/**
			 * 按下 ESC 关闭弹窗
			 *
			 * @param {KeyboardEvent} event 键盘事件
			 */
			handleKeydown(event) {
				if (event.key === 'Escape') {
					this.$emit('close')
				}
			}
		}
	}
</script>

<style scoped>
	.gd {
		position: fixed;
		inset: 0;
		z-index: 9998;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 24px;
		background: rgba(3, 6, 20, 0.55);
		backdrop-filter: blur(8px);
	}

	.gd__panel {
		display: flex;
		flex-direction: column;
		width: min(880px, 94vw);
		max-height: 82vh;
		padding: 20px 22px 22px;
		border-radius: 24px;
		background: linear-gradient(180deg, rgba(38, 44, 76, 0.96), rgba(26, 30, 54, 0.96));
		border: 1px solid rgba(255, 255, 255, 0.12);
		box-shadow: 0 30px 70px rgba(0, 0, 0, 0.6);
	}

	.gd__head {
		display: flex;
		align-items: center;
		gap: 10px;
		flex: none;
		margin-bottom: 16px;
	}

	.gd__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: none;
		width: 34px;
		height: 34px;
		border-radius: 11px;
		background: linear-gradient(135deg, rgba(120, 150, 255, 0.26), rgba(190, 110, 255, 0.26));
		color: #dfe5ff;
		font-size: 17px;
	}

	.gd__icon img {
		width: 22px;
		height: 22px;
		object-fit: contain;
		border-radius: 5px;
	}

	.gd__title {
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: #eef2ff;
		font-size: 18px;
		font-weight: 600;
	}

	.gd__count {
		flex: none;
		padding: 3px 10px;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.1);
		color: rgba(206, 216, 250, 0.72);
		font-size: 12px;
	}

	.gd__search {
		flex: 1;
		min-width: 120px;
		margin-left: auto;
	}

	.gd__search input {
		width: 100%;
		max-width: 220px;
		margin-left: auto;
		padding: 8px 14px;
		border: 1px solid rgba(255, 255, 255, 0.14);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.07);
		color: #eaf0ff;
		font: inherit;
		font-size: 13px;
		outline: none;
		transition: border-color 0.2s ease, background 0.2s ease;
	}

	.gd__search input::placeholder {
		color: rgba(180, 192, 235, 0.5);
	}

	.gd__search input:focus {
		border-color: rgba(140, 164, 255, 0.5);
		background: rgba(255, 255, 255, 0.11);
	}

	.gd__close {
		flex: none;
		width: 30px;
		height: 30px;
		border: none;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.1);
		color: rgba(226, 233, 255, 0.8);
		cursor: pointer;
		font-size: 13px;
		line-height: 1;
		transition: background 0.2s ease;
	}

	.gd__close:hover {
		background: rgba(255, 255, 255, 0.2);
	}

	.gd__body {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		padding: 4px 2px 8px;
	}

	.gd__body::-webkit-scrollbar {
		width: 8px;
	}

	.gd__body::-webkit-scrollbar-thumb {
		border-radius: 4px;
		background: rgba(255, 255, 255, 0.16);
	}

	.gd__grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
		gap: 12px;
	}

	.gd__add {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		min-height: 118px;
		border: 1px dashed rgba(255, 255, 255, 0.22);
		border-radius: 20px;
		background: rgba(255, 255, 255, 0.035);
		color: rgba(200, 212, 248, 0.55);
		cursor: pointer;
		font-size: 12px;
		transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.gd__add:hover {
		border-color: rgba(140, 164, 255, 0.55);
		background: rgba(140, 164, 255, 0.1);
		color: #c3cfff;
	}

	.gd__add-plus {
		color: rgba(190, 204, 245, 0.6);
		font-size: 22px;
		line-height: 1;
	}

	.gd__empty {
		padding: 60px 0;
		margin: 0;
		color: rgba(196, 208, 245, 0.55);
		font-size: 14px;
		text-align: center;
	}

	.gd-fade-enter-active,
	.gd-fade-leave-active {
		transition: opacity 0.22s ease;
	}

	.gd-fade-enter,
	.gd-fade-leave-to {
		opacity: 0;
	}

	.gd-fade-enter-active .gd__panel {
		transition: transform 0.22s ease;
	}

	.gd-fade-enter .gd__panel {
		transform: translateY(14px) scale(0.98);
	}

	@media (max-width: 640px) {
		.gd {
			padding: 14px;
		}

		.gd__head {
			flex-wrap: wrap;
		}

		.gd__search {
			order: 5;
			width: 100%;
			margin-left: 0;
		}

		.gd__search input {
			max-width: none;
			margin-left: 0;
		}

		.gd__grid {
			grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
		}
	}
</style>
